import React from 'react'
import { Card } from '../ui'

/**
 * Props interface for the AboutNavigation component
 */
export interface AboutNavigationProps {
  /** CSS class name for custom styling */
  className?: string
}

/**
 * Navigation data for the About page sections
 */
const NAV_ITEMS = [
  { id: 'intro', label: 'Introduction', href: '#intro' },
  { id: 'skills', label: 'Skills', href: '#skills' },
  { id: 'philosophy', label: 'Philosophy', href: '#philosophy' },
  { id: 'journey', label: 'Journey', href: '#journey' },
  { id: 'achievements', label: 'Achievements', href: '#achievements' },
  { id: 'contact', label: 'Contact', href: '#contact' },
] as const

/**
 * Navigation component for smooth scrolling to different sections of the About page.
 *
 * Features:
 * - Sticky navigation that stays visible while scrolling
 * - Smooth scrolling to page sections
 * - Keyboard accessible navigation
 * - Responsive design
 * - Skip link for accessibility
 */
export const AboutNavigation: React.FC<AboutNavigationProps> = ({
  className = '',
}) => {
  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    e.preventDefault()
    const targetId = href.replace('#', '')
    const targetElement = document.getElementById(targetId)

    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })

      // Update URL without triggering navigation
      window.history.replaceState(null, '', href)

      // Focus the target element for screen readers
      targetElement.focus({ preventScroll: true })
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent, href: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      const fakeEvent = {
        preventDefault: () => {},
        currentTarget: e.currentTarget,
      } as React.MouseEvent<HTMLAnchorElement>
      handleNavClick(fakeEvent, href)
    }
  }

  return (
    <nav
      className={`about-navigation ${className}`}
      aria-label="About page sections"
    >
      <Card variant="glass" padding="sm">
        <ul className="nav-list" role="menubar" aria-label="Page navigation">
          {NAV_ITEMS.map(item => (
            <li key={item.id} className="nav-item" role="none">
              <a
                href={item.href}
                className="nav-link"
                role="menuitem"
                aria-label={`Navigate to ${item.label} section`}
                onClick={e => handleNavClick(e, item.href)}
                onKeyDown={e => handleKeyDown(e, item.href)}
              >
                {item.label}
              </a>
            </li>
          ))}
        </ul>
      </Card>
    </nav>
  )
}
